import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import { Spinner } from 'react-bootstrap';
import { debounce } from 'lodash';
import { FaMapMarkerAlt, FaSearch } from 'react-icons/fa';

import axios from 'axios';
import './Styles.css';
import { useAgentDetails } from '../utils/useAgentDetails';
import AgentAuthorRow from '../components/AgentAuthorRow';

function SuburbPreferences() {
  const { agent_slug, agentDetails, setAgentDetails, branding } = useAgentDetails();
  const [email, setEmail] = useState('');
  const [selectedSuburbs, setSelectedSuburbs] = useState([]);
  const [message, setMessage] = useState('');
  const [key, setKey] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);

  const { accentColor, buttonTextColor, isLight, iconColor, iconBgColor } = branding || {};

  useEffect(() => {
    if (agentDetails) {
      document.title = `${agentDetails.agency_name_short} Suburb Preferences`;
    }
  }, [agentDetails]);

  useEffect(() => {
    if (agentDetails) {
      setEmail(agentDetails.user_email);
    }
  }, [agentDetails]);  

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!email) {
        setMessage('Please submit your email so we can update your preferences.');
        return;
    }            
    setIsUpdating(true);
    fetch(`${window.api_host}/update-suburb-preferences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        suburbs: selectedSuburbs.map(suburb => suburb.value),
        agency_id: agentDetails.agency_id,
        agent_id: agentDetails.agent_id
      })
    })
    .then(response => response.json())
    .then(data => {
      setIsUpdating(false);
      if (data.status === 'success') {
        setSelectedSuburbs([]);
        setMessage('Your preferences have been updated successfully.');
        setKey(prevKey => prevKey + 1);
      } else {
        setMessage('There was an error updating your preferences. Please try again.');
      }
    })
    .catch(error => {
      setIsUpdating(false);
      console.error('Error:', error);
      setMessage('There was an error updating your preferences. Please try again.');
    });
  };

  const loadSuburbs = debounce((inputValue, callback) => {
    axios.get(`${window.api_host}/location-search?q=${inputValue}`)
    .then(response => {
        const options = response.data
          .filter(r => r.type === 'suburb')
          .map(suburb => ({
            label: suburb.label,
            value: suburb.value,
          }));
        callback(options);
    })
    .catch(error => {
        console.error('Error loading suburb options:', error);
        callback([]);
    });
}, 500);

  if (!agentDetails) {
    return (
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}>
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0,
        background: 'linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)',
      }} />

      <Container style={{ maxWidth: 560, position: 'relative', zIndex: 2 }}>
        <AgentAuthorRow agentDetails={agentDetails} />

        <div style={{ textAlign: 'center', marginBottom: 32, marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              backgroundColor: `${iconBgColor}14`,
              border: `1px solid ${isLight ? '#222' : accentColor}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <FaMapMarkerAlt size={22} style={{ color: iconColor }} />
            </div>
            <h1 style={{
              fontSize: '1.6rem', fontWeight: 700, color: '#111',
              margin: 0, letterSpacing: '-0.02em',
            }}>
              Suburb Preferences
            </h1>
          </div>
          <p style={{
            fontSize: '0.92rem', color: '#6b7280', marginBottom: 0,
            lineHeight: 1.6, maxWidth: 420, margin: '0 auto',
          }}>
            Update your preferred suburbs below. We will use this information to provide you with more relevant monthly suburb updates.
          </p>
        </div>

        <div style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          boxShadow: '0 8px 28px rgba(15, 23, 42, 0.08), 0 2px 6px rgba(15, 23, 42, 0.04)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          padding: '28px 28px',
        }}>
          <div style={{
            fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase',
            letterSpacing: '0.12em', fontWeight: 600, marginBottom: 12,
          }}>
            <FaSearch size={10} style={{ marginRight: 6 }} />
            Update preferences
          </div>

          <Form onSubmit={handleSubmit}>
            <Form.Control
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{
                borderRadius: 12,
                border: '1px solid #e5e7eb',
                padding: '12px 14px',
                fontSize: '0.88rem',
                marginBottom: 10,
              }}
            />

            <AsyncSelect              
              key={key}
              isMulti
              cacheOptions
              loadOptions={loadSuburbs}
              onChange={setSelectedSuburbs}
              noOptionsMessage={() => null}
              placeholder="Search by suburb or postcode..."
              openMenuOnClick={false}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderRadius: 12,
                  borderColor: state.isFocused ? accentColor : '#e5e7eb',
                  boxShadow: state.isFocused ? `0 0 0 1px ${accentColor}` : 'none',
                  minHeight: 48,
                  fontSize: '0.88rem',
                  '&:hover': { borderColor: accentColor },
                }),
                placeholder: (provided) => ({
                  ...provided,
                  textAlign: 'left',
                  color: '#c4c4c4',
                }),
                menu: (provided) => ({
                  ...provided,
                  textAlign: 'left',
                  borderRadius: 12,
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isFocused ? `${accentColor}0a` : '#fff',
                  color: '#1a1a1a',
                  fontSize: '0.88rem',
                  '&:active': { backgroundColor: `${accentColor}18` },
                }),
                singleValue: (provided) => ({
                  ...provided,
                  textAlign: 'left',
                  fontWeight: 500,
                  color: '#111',
                }),
                multiValue: (provided) => ({
                  ...provided,
                  borderRadius: 8,
                  backgroundColor: `${accentColor}12`,
                }),
                multiValueLabel: (provided) => ({
                  ...provided,
                  color: '#111',
                  fontSize: '0.82rem',
                }),
              }}
            />

            <Button
              type="submit"
              disabled={isUpdating}
              style={{
                width: '100%',
                backgroundColor: accentColor,
                borderColor: isLight ? '#222' : accentColor,
                color: buttonTextColor,
                borderRadius: 12,
                padding: '14px 0',
                fontWeight: 600,
                fontSize: '0.95rem',
                boxShadow: `0 4px 16px ${accentColor}30`,
                marginTop: 16,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              {isUpdating ? <Spinner animation="border" size="sm" /> : 'Update your suburb preferences'}
            </Button>

            {message && (
              <Alert variant="secondary" className="mt-3" style={{ borderRadius: 12, border: 'none', backgroundColor: '#f9fafb', color: '#6b7280', fontSize: '0.88rem', textAlign: 'center' }}>
                {message}
              </Alert>
            )}
          </Form>
        </div>
      </Container>
    </div>
  );
}

export default SuburbPreferences;